import { Popover } from "@mui/material";
import * as React from "react";

import { usePopupState } from "@/shared/hooks/usePopupState";

import { Tag } from "../Tag";

import styles from "./TagGroup.module.css";

interface TagGroupProps {
  tags: string[];

  /**
   * Max pills to show before collapsing the remainder into a "+N" indicator.
   * Omit to show every tag.
   */
  maxDisplayCount?: number;

  /** Callback when a user clicks on a tag */
  onClick?: (tag: string) => void;
}

export function TagGroup(props: TagGroupProps) {
  const { tags, maxDisplayCount } = props;

  const sorted = React.useMemo(() => [...tags].sort(), [tags]);
  const shown =
    maxDisplayCount !== undefined ? sorted.slice(0, maxDisplayCount) : sorted;
  const hidden = sorted.slice(shown.length);
  const overflow = hidden.length;

  const popup = usePopupState();

  return (
    <span className={styles.list}>
      {shown.map((tag) => (
        <Tag key={tag} label={tag} />
      ))}
      {overflow > 0 && (
        <>
          <button
            type="button"
            className={styles.more}
            aria-haspopup="dialog"
            aria-expanded={popup.isOpen}
            aria-label={`Show ${overflow} more ${overflow === 1 ? "tag" : "tags"}`}
            onClick={(event) => {
              // Don't let the click bubble to the grid row (select / navigate).
              event.stopPropagation();
              popup.open({ anchorEl: event.currentTarget });
            }}
          >
            +{overflow}
          </button>
          <Popover
            open={popup.isOpen}
            anchorEl={popup.anchor}
            onClose={popup.close}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            slotProps={{ paper: { className: styles.popover } }}
          >
            <div className={styles.popoverList}>
              {hidden.map((tag) => (
                <Tag key={tag} label={tag} />
              ))}
            </div>
          </Popover>
        </>
      )}
    </span>
  );
}
